/// <reference types="@types/gapi" />
/// <reference types="@types/google.picker" />
import { importExternalScript } from '../../utils/importExternalScript.ts';

import type { AuthData } from './GoogleAuth.ts';
// Full list of available fields: https://developers.google.com/picker/docs/reference#document
export type GoogleDrivePickerDoc = {
	id: string;
	name: string;
	lastEditedUtc: number;
	size?: number;
};

declare global {
	interface Window {
		gadgets: object | null;
	}
}

/**
 * The class is a wrapper over Google Picker:
 * https://developers.google.com/picker
 * */
export class GoogleDrivePicker {
	private credentials: AuthData;

	pickerInstance: google.picker.Picker | null = null;
	googlePickerAPI: typeof google.picker | null = null;
	gadgets: typeof globalThis.gadgets | null = null;
	googleScript: HTMLScriptElement | null = null;

	constructor(credentials: AuthData) {
		this.credentials = credentials;
	}

	async init(): Promise<this> {
		await this.loadAPI();

		return this;
	}

	showPicker(): Promise<GoogleDrivePickerDoc[] | null> {
		return new Promise((resolve, reject) => {
			if (!this.googlePickerAPI) {
				return reject(new Error('google.picker API was not loaded yet'));
			}

			const view = new this.googlePickerAPI.DocsView(this.googlePickerAPI.ViewId.DOCUMENTS);
			// Show only GoogleDocs, exclude Word documents
			view.setMimeTypes('application/vnd.google-apps.document');

			this.pickerInstance = new this.googlePickerAPI.PickerBuilder()
				.enableFeature(this.googlePickerAPI.Feature.NAV_HIDDEN)
				.enableFeature(this.googlePickerAPI.Feature.MULTISELECT_ENABLED)
				.setAppId(this.credentials.appId)
				.setOAuthToken(this.credentials.oauthToken)
				.addView(view)
				.setDeveloperKey(this.credentials.apiKey)
				// Note, that setCallback will be called several times on different events inside the Picker
				.setCallback((data) => this.performPickerResult(data, resolve, reject))
				.build();

			this.pickerInstance.setVisible(true);
		});
	}

	showPickerWithoutKey(): Promise<GoogleDrivePickerDoc[] | null> {
		return new Promise((resolve, reject) => {
			if (!this.googlePickerAPI) {
				return reject(new Error('google.picker API was not loaded yet'));
			}

			const view = new this.googlePickerAPI.DocsView(this.googlePickerAPI.ViewId.DOCUMENTS);
			// filter for only Google docs
			view.setMimeTypes('application/vnd.google-apps.document');

			this.pickerInstance = new this.googlePickerAPI.PickerBuilder()
				.enableFeature(this.googlePickerAPI.Feature.NAV_HIDDEN)
				.enableFeature(this.googlePickerAPI.Feature.MULTISELECT_ENABLED)
				.setAppId(this.credentials.appId)
				.setOAuthToken(this.credentials.oauthToken)
				// omit setDeveloperKey call since it seems unnecessary for displaying the file picker
				.addView(view)
				.setCallback((data) => this.performPickerResult(data, resolve, reject))
				.build();

			this.pickerInstance.setVisible(true);
		});
	}

	private performPickerResult(
		data: any,
		onSuccess: (doc: GoogleDrivePickerDoc[] | null) => any,
		onError: (err: Error) => any,
	): void {
		if (!this.googlePickerAPI) {
			onError(new Error('google.picker API was not loaded yet'));
			return;
		}

		const buttonActions = [this.googlePickerAPI.Action.PICKED, this.googlePickerAPI.Action.CANCEL];
		if (!buttonActions.includes(data.action)) {
			return;
		}

		if (data.action === this.googlePickerAPI.Action.PICKED) {
			if (!data.docs || !data.docs[0]) {
				onError(new Error('Failed to pick a file'));
			} else {
				onSuccess(data.docs);
			}
		} else if (data.action === this.googlePickerAPI.Action.CANCEL) {
			onSuccess(null);
		}

		this.addGadgetsToGlobalScope();
	}

	private async loadAPI() {
		const loadApiPromise = async (): Promise<typeof google.picker> => {
			this.removeGadgetsFromGlobalScope();
			this.googleScript = await importExternalScript('https://apis.google.com/js/api.js');

			return new Promise((resolve) => {
				gapi.load('picker', () => {
					resolve(google.picker);
				});
			});
		};

		this.googlePickerAPI = await loadApiPromise();
	}

	/*
  this is workaround to make Google API works properly.
  variable "gadgets" doesn't allow gapi.load('picker') works properly because of global scope pollution and
  "gapi" cannot initialize global "google" variable with "picker" field inside.
  this problem occurred after https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence-frontend/pull-requests/12152/overview
  was merged into master. The PR linked above is a fix for editor gadgets which interact with Jira and it is very important.
   */

	removeGadgetsFromGlobalScope(): void {
		this.gadgets = globalThis.gadgets;
		globalThis.gadgets = null;
	}

	addGadgetsToGlobalScope(): void {
		globalThis.gadgets = this.gadgets;
		this.gadgets = null;
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.body.removeChild(this.googleScript as Node);
		this.googleScript = null;
	}
}
